/**
 * <SubscriptionNoticeCard> — un avis daté qui ouvre quelque chose (molecule, L0.1).
 *
 * La carte « Renouvellement à venir » du portail (`ParentSubscriptionPortalScreen.js:695-736`) :
 * un glyphe, un titre, la phrase qui le date, et un chevron — la carte entière est le bouton.
 *
 * Deux lignes de texte, donc pas un `<SettingRow>` (dont la valeur tient sur une ligne et ne se
 * replie pas). Composition de `<Card pressable>` + `<Icon>` + `<Text>` ; aucun fond dessiné à la main.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Card } from "../Card"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

export type SubscriptionNoticeCardProps = {
    title: string
    /** La phrase qui date l'avis — « Fin d'engagement le 15 novembre 2026. Voir les options. » */
    message: string
    /** Glyphe de tête — défaut `bell`. */
    iconName?: IconName
    /** Couleur du glyphe et du liseré — défaut `warning`. */
    color?: SemanticColorKey
    onPress: () => void
    testID?: string
}

export function SubscriptionNoticeCard(props: SubscriptionNoticeCardProps) {
    const { title, message, iconName = "bell", color = "warning", onPress, testID } = props

    return (
        <Card pressable onPress={onPress} leftBorderColor={color} p="4" testID={testID}>
            <Row gap="3" align="start">
                <Icon name={iconName} size={20} color={color} />
                <Stack gap="1" style={styles.body}>
                    <Text size="text-md" weight="bold">
                        {title}
                    </Text>
                    <Text size="text-sm" color="text-muted">
                        {message}
                    </Text>
                </Stack>
                <Icon name="chevron-right" size={16} color="text-muted" />
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    // Le bloc de texte prend la place restante ; le chevron reste collé à droite.
    body: { flexShrink: 1, flexGrow: 1 },
}))
